import { useEffect, useState } from 'react'
import { AlertTriangle, BarChart2 } from 'lucide-react'
import { Card } from './MetricCards'
import ReviewVolumeChart from './ReviewVolumeChart'
import RatingTrendChart from './RatingTrendChart'
import ValidationScoreChart from './ValidationScoreChart'
import CategoryBreakdownChart from './CategoryBreakdownChart'
import { getAnalyticsOverview } from '../../services/dashboardApi'
import type { AnalyticsOverview } from '../../types/dashboard'

const PERIODS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
] as const

const RESPONSE_QUALITY_LABELS: Record<string, string> = {
  ai_generated: 'AI-generated',
  fallback_template: 'Fallback template',
}

const ESCALATION_REASON_LABELS: Record<string, string> = {
  low_validation_score: 'Low validation score',
  keyword_match: 'Keyword match',
  auto_respond_off: 'Auto-respond off',
  manual: 'Manual',
}

// Fixed order, validated against the app's dark card surface (#12121a) —
// see the dataviz skill's categorical color-formula checks.
const ESCALATION_COLORS = ['#3987e5', '#199e70', '#c98500', '#9085e9']
const RESPONSE_QUALITY_COLORS = ['#3987e5', '#199e70']

interface AnalyticsPageProps {
  apiKey: string
  locationId: string
}

export default function AnalyticsPage({ apiKey, locationId }: AnalyticsPageProps) {
  const [days, setDays] = useState<number>(30)
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!apiKey || !locationId) return
    let cancelled = false
    setIsLoading(true)
    setError(null)
    getAnalyticsOverview(apiKey, locationId, days)
      .then((data) => { if (!cancelled) setOverview(data) })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load analytics') })
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [apiKey, locationId, days])

  const periodSelector = (
    <div className="flex items-center gap-1 bg-rd-surface2 border border-white/[0.08] rounded-lg p-0.5">
      {PERIODS.map((p) => (
        <button
          key={p.days}
          type="button"
          onClick={() => setDays(p.days)}
          className={`px-2.5 py-1 rounded-md text-[12px] font-medium transition-colors ${
            days === p.days ? 'bg-rd-accent/10 text-rd-accent' : 'text-rd-muted hover:text-rd-text'
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  )

  if (isLoading) {
    return (
      <div className="flex-1 overflow-hidden flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between flex-shrink-0">
          <p className="text-sm font-medium text-rd-text">Analytics</p>
          {periodSelector}
        </div>
        <div className="grid grid-cols-5 gap-2.5 flex-shrink-0">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-rd-surface border border-white/[0.07] rounded-xl p-4 animate-pulse">
              <div className="h-3 w-24 bg-white/5 rounded mb-3" />
              <div className="h-7 w-16 bg-white/5 rounded" />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="bg-rd-surface border border-white/[0.07] rounded-xl p-4 h-[232px] animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 p-4" role="alert">
        <AlertTriangle className="w-8 h-8 text-warning-500" />
        <p className="text-rd-muted text-sm">{error}</p>
      </div>
    )
  }

  if (!overview) return null

  if (overview.summary.total_reviews === 0) {
    return (
      <div className="flex-1 overflow-hidden flex flex-col gap-3 p-4">
        <div className="flex items-center justify-between flex-shrink-0">
          <p className="text-sm font-medium text-rd-text">Analytics</p>
          {periodSelector}
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <BarChart2 className="w-8 h-8 text-rd-dim" />
          <p className="text-rd-muted text-sm">No review activity in this period.</p>
        </div>
      </div>
    )
  }

  const escalationData = overview.escalation_reasons.map((r) => ({
    label: ESCALATION_REASON_LABELS[r.reason] ?? r.reason,
    count: r.count,
  }))
  const responseQualityData = overview.response_quality.map((q) => ({
    label: RESPONSE_QUALITY_LABELS[q.category] ?? q.category,
    count: q.count,
  }))

  return (
    <div className="flex-1 overflow-y-auto flex flex-col gap-3 p-4">
      <div className="flex items-center justify-between flex-shrink-0">
        <p className="text-sm font-medium text-rd-text">Analytics</p>
        {periodSelector}
      </div>

      <div className="grid grid-cols-5 gap-2.5 flex-shrink-0">
        <Card label="Total reviews" value={overview.summary.total_reviews} sub={`Last ${overview.period_days} days`} />
        <Card
          label="Avg. rating"
          value={overview.summary.avg_rating > 0 ? overview.summary.avg_rating.toFixed(1) : '—'}
          sub="Out of 5.0"
        />
        <Card label="Response rate" value={`${overview.summary.response_rate_percent.toFixed(0)}%`} sub="Responded" />
        <Card
          label="Escalation rate"
          value={`${overview.summary.escalation_rate_percent.toFixed(0)}%`}
          sub="Needed a human"
          valueColor={overview.summary.escalation_rate_percent > 0 ? 'text-danger-500' : 'text-rd-text'}
        />
        <Card
          label="Avg. validation score"
          value={overview.summary.avg_validation_score > 0 ? overview.summary.avg_validation_score.toFixed(0) : '—'}
          sub={`Min threshold ${overview.min_validation_score}`}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <ReviewVolumeChart data={overview.daily_trend} />
        <RatingTrendChart data={overview.daily_trend} />
        <ValidationScoreChart data={overview.validation_trend} minValidationScore={overview.min_validation_score} />
        <div className="grid grid-cols-2 gap-3">
          <CategoryBreakdownChart title="Escalation reasons" data={escalationData} colors={ESCALATION_COLORS} />
          <CategoryBreakdownChart title="Response quality" data={responseQualityData} colors={RESPONSE_QUALITY_COLORS} />
        </div>
      </div>
    </div>
  )
}
